import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { HexColorPicker } from "react-colorful"
import { Popover } from "@mui/material"

import Dialog from "@/components/dialog/Dialog"
import BasicButton from "@/components/button/BasicButton"
import TextButton from "@/components/button/TextButton"
import Icon from "@/components/icon/Icon"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import { colors } from "@/components/colors"
import { MetricThreshold, ThresholdOperator, ThresholdRule } from "@/types/threshold"
import { getThresholdCellStyle } from "@/utils/threshold"

import "./thresholdDialog.scss"

const PALETTE_COLORS = (
  ["sky", "blue", "purple", "pink", "red", "yellow", "lime", "green"] as const
).flatMap((name) =>
  (["500", "700", "900"] as const).map((shade) => ({
    color: (colors[name] as unknown as Record<string, string>)[shade],
    name: `${name}-${shade}`,
  })),
)

function ThresholdColorPicker({
  color,
  onChange,
}: {
  color: string
  onChange: (color: string) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [hexInput, setHexInput] = useState(color.replace("#", ""))
  const anchorRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    setHexInput(color.replace("#", "").toUpperCase())
  }, [color])

  const handleHexChange = (value: string) => {
    const cleaned = value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6)
    setHexInput(cleaned.toUpperCase())
    if (cleaned.length === 6) {
      onChange(`#${cleaned}`)
    }
  }

  return (
    <>
      <span
        ref={anchorRef}
        className="threshold-color-swatch"
        style={{ backgroundColor: color }}
        onClick={() => setOpen(true)}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { className: "ThresholdColorPickerPopover" } }}
        style={{ zIndex: 1500 }}>
        <div className="threshold-colorpicker">
          <div className="threshold-colorpicker__header">
            <p>{t("colorPicker")}</p>
            <button type="button" onClick={() => setOpen(false)}>
              <Icon type="close" size={16} color={colors.neutral.grey[600]} />
            </button>
          </div>
          <HexColorPicker color={color} onChange={onChange} />
          <div className="threshold-colorpicker__hex-row">
            <span className="threshold-colorpicker__hex-label">#</span>
            <input
              className="threshold-colorpicker__hex-input"
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              maxLength={6}
            />
          </div>
          <div className="threshold-colorpicker__saved">
            <p>{t("savedColors")}</p>
            <div className="threshold-colorpicker__palette">
              {PALETTE_COLORS.map((item) => (
                <div
                  key={item.name}
                  className={`threshold-palette-chip ${color === item.color ? "active" : ""}`}
                  style={{ backgroundColor: item.color }}
                  onClick={() => onChange(item.color)}
                />
              ))}
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

const OPERATOR_OPTIONS = [
  { label: ">", value: ">" },
  { label: ">=", value: ">=" },
  { label: "<", value: "<" },
  { label: "<=", value: "<=" },
  { label: "=", value: "==" },
  { label: "Between", value: "between" },
]

function generatePreviewValues(rules: ThresholdRule[]): number[] {
  if (rules.length === 0) {
    return []
  }

  const ruleValues: number[] = []
  rules.forEach((rule) => {
    ruleValues.push(rule.value)
    if (rule.operator === "between" && rule.valueTo !== undefined) {
      ruleValues.push(rule.valueTo)
    }
  })

  const min = Math.min(...ruleValues)
  const max = Math.max(...ruleValues)
  const range = max - min || max || 100

  // 룰 경계값 아래, 사이, 위를 골고루 포함
  const step = Math.max(1, Math.round(range * 0.25))
  const values = new Set<number>()

  values.add(Math.max(0, min - step)) // 경계 아래
  ruleValues.forEach((v) => {
    values.add(v - 1) // 경계 직전
    values.add(v) // 경계 정확히
    values.add(v + 1) // 경계 직후
  })
  values.add(max + step) // 경계 위

  return [...values]
    .filter((v) => v >= 0)
    .sort((a, b) => a - b)
    .slice(0, 8)
}

const DEFAULT_RULE: ThresholdRule = {
  operator: ">=",
  value: 0,
  color: colors.red["700"],
}

type ThresholdDialogProps = {
  open: boolean
  onClose: () => void
  metricKey: string
  metricName: string
  threshold?: MetricThreshold
  onSave: (threshold: MetricThreshold) => void
}

export default function ThresholdDialog({
  open,
  onClose,
  metricKey,
  metricName,
  threshold,
  onSave,
}: ThresholdDialogProps) {
  const { t } = useTranslation()
  const [rules, setRules] = useState<ThresholdRule[]>([])
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    if (open) {
      setRules(threshold?.rules ? [...threshold.rules] : [])
      setEnabled(threshold?.enabled ?? true)
    }
  }, [open, threshold])

  const handleAddRule = () => {
    setRules((prev) => [...prev, { ...DEFAULT_RULE }])
  }

  const handleRemoveRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpdateRule = (index: number, updates: Partial<ThresholdRule>) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...updates } : rule)))
  }

  const handleSave = () => {
    onSave({ metricKey, rules, enabled })
    onClose()
  }

  const handleClearAll = () => {
    onSave({ metricKey, rules: [], enabled: false })
    onClose()
  }

  const previewThreshold: MetricThreshold = { metricKey, rules, enabled: true }
  const previewValues = useMemo(() => generatePreviewValues(rules), [rules])

  return (
    <Dialog
      open={open}
      title={t("thresholdSettings")}
      onClose={onClose}
      maxWidth="600px"
      footer={
        <div className="ThresholdDialog__footer">
          <BasicButton type="default-outlined" size="md" onClick={onClose}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton type="primary-contained" size="md" onClick={handleSave}>
            {t("button.save")}
          </BasicButton>
        </div>
      }>
      <div className="ThresholdDialog">
        <p className="threshold-metric-name">{metricName}</p>

        {rules.length === 0 ? (
          <p className="threshold-empty-msg">{t("messages.noDataAvailable")}</p>
        ) : (
          <div className="threshold-rules-list">
            {rules.map((rule, index) => (
              <div key={index} className="threshold-rule-row">
                <div className="threshold-rule-controls">
                  <div className="threshold-rule-fields">
                    <div className="threshold-rule-operator">
                      <span className="threshold-rule-label">{t("operator")}</span>
                      <Selector
                        options={OPERATOR_OPTIONS}
                        value={OPERATOR_OPTIONS.find((op) => op.value === rule.operator) || null}
                        isOptionEqualToValue={(option: { value: string }, val: { value: string }) =>
                          option.value === val.value
                        }
                        onChange={(_: unknown, val: { label: string; value: string } | null) => {
                          const operator = (val?.value || ">=") as ThresholdOperator
                          handleUpdateRule(index, {
                            operator,
                            ...(operator === "between" && rule.valueTo === undefined
                              ? { valueTo: rule.value }
                              : {}),
                          })
                        }}
                        fullWidth
                      />
                    </div>

                    <div className="threshold-rule-value-group">
                      <div className="threshold-rule-value">
                        <span className="threshold-rule-label">{t("value")}</span>
                        <TextField
                          type="number"
                          value={String(rule.value)}
                          onChange={(e) =>
                            handleUpdateRule(index, { value: parseFloat(e.target.value) || 0 })
                          }
                        />
                      </div>

                      {rule.operator === "between" && (
                        <>
                          <span className="threshold-rule-to-label">~</span>
                          <div className="threshold-rule-value">
                            <TextField
                              type="number"
                              value={String(rule.valueTo ?? 0)}
                              onChange={(e) =>
                                handleUpdateRule(index, {
                                  valueTo: parseFloat(e.target.value) || 0,
                                })
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <div className="threshold-rule-color">
                      <span className="threshold-rule-label">{t("color")}</span>
                      <ThresholdColorPicker
                        color={rule.color}
                        onChange={(color) => handleUpdateRule(index, { color })}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="threshold-remove-btn"
                    onClick={() => handleRemoveRule(index)}>
                    <Icon type="close" size={20} color={colors.platinum[500]} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="threshold-action-row">
          <div className="threshold-add-rule">
            <BasicButton type="default-outlined" size="md" onClick={handleAddRule}>
              <Icon type="add" size={20} style={{ opacity: 0.7 }} />
              {t("thresholdAddRule")}
            </BasicButton>
          </div>
          {rules.length > 0 && (
            <TextButton variant="link" onClick={handleClearAll}>
              {t("button.clearAll")}
            </TextButton>
          )}
        </div>

        {rules.length > 0 && <div className="threshold-divider" />}

        {rules.length > 0 && (
          <div className="threshold-preview-section">
            <p className="threshold-preview-title">{t("thresholdPreview")}</p>
            <div className="threshold-preview-cells">
              {previewValues.map((val) => {
                const style = getThresholdCellStyle(val, previewThreshold)

                return (
                  <div
                    key={val}
                    className="threshold-preview-cell"
                    style={
                      style
                        ? { backgroundColor: style.backgroundColor, color: style.color }
                        : undefined
                    }>
                    {val}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
